import { CdkDrag, CdkDragEnd, CdkDragHandle, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { Note, NoteStateMove, NoteValueChange, Point } from '../../../core/model/datatypes';
import { NotesService } from '../../../core/provider/notes.service';
import { StateService } from '../../../core/provider/state.service';
import { ZoomService } from '../../../core/provider/zoom.service';

@Component({
  selector: 'app-note',
  templateUrl: './note.component.html',
  styleUrls: ['./note.component.scss'],
  imports: [CdkDrag, MatButtonModule, CdkDragHandle, MatIconButton, MatTooltip, MatFormField, MatLabel, MatInput, ReactiveFormsModule, MatSuffix, CdkTextareaAutosize]
})
export class NoteComponent implements OnInit {
  private notes = inject(NotesService);
  private zs = inject(ZoomService);
  private ss = inject(StateService);
  private fb = inject(FormBuilder);

  //generated by the note service
  @Input() id: number;
  @Output() deleteNote: any = new EventEmitter();
  @Output() saveNoteText: any = new EventEmitter();
  @Output() onNoteMoved: any = new EventEmitter();

  note: Note;
  noteForm: FormGroup;

  starting_topleft: Point = { x: 0, y: 0 };
  topleft: Point = { x: 0, y: 0 };
  markdown: string = "";
  show_url: boolean = false;
  canvas: HTMLCanvasElement;
  cx: any;
  disable_drag: boolean = false;
  image_url: string = "";
  moveSubscription: Subscription;
  offset: Point = null;

  before: Note = null;

  ngOnInit() {
    this.note = this.notes.get(this.id);

    this.before = this.notes.copyNote(this.note);

    if (this.note == undefined) {
      this.topleft = { x: 0, y: 0 };
    } else {
      this.topleft = {
        x: this.note.topleft.x,
        y: this.note.topleft.y
      }
    }

    // Initialize reactive form
    this.noteForm = this.fb.group({
      title: [this.note?.title || '', Validators.required],
      text: [this.note?.text || ''],
      imageurl: [this.note?.imageurl || '']
    });

    this.noteForm.valueChanges.subscribe(() => {
      this.note.title = this.noteForm.get('title')?.value || '';
      this.note.text = this.noteForm.get('text')?.value || '';
      this.note.imageurl = this.noteForm.get('imageurl')?.value || '';
    });
  }

  ngAfterViewInit() {
    this.setPosition(this.topleft)

  }

  getPosition(): Point {
    return this.topleft;
  }

  setPosition(topleft: Point) {
    this.topleft = { x: topleft.x, y: topleft.y };
    this.canvas = <HTMLCanvasElement>document.getElementById("notecanvas-" + this.note.id.toString());
    this.cx = this.canvas.getContext("2d");
    let note_container = document.getElementById('note-' + this.id);
    note_container.style.transform = 'none'; //negate angulars default positioning mechanism
    note_container.style.top = this.topleft.y + "px";
    note_container.style.left = this.topleft.x + "px";
  }


  delete() {
    const change: NoteValueChange = {
      originator: 'NOTE',
      type: 'REMOVED',
      id: this.id,
      before: this.notes.get(this.id),
      after: null
    }
    this.ss.addStateChange(change);
    this.deleteNote.emit(this.note.id);
  }

  dragStart($event: CdkDragStart) {
    this.offset = null;
    this.starting_topleft = {
      x: this.topleft.x,
      y: this.topleft.y
    }
  }


  dragMove($event: CdkDragMove) {

    let parent = document.getElementById('scrollable-container');
    let note_container = document.getElementById('note-' + this.id);
    let rect_palette = parent.getBoundingClientRect();


    const zoom_factor = 1 / this.zs.getMixerZoom();

    //this gives the position of
    let op_topleft_inscale = {
      x: note_container.offsetLeft,
      y: note_container.offsetTop
    }


    let scaled_pointer = {
      x: ($event.pointerPosition.x - rect_palette.x + parent.scrollLeft) * zoom_factor,
      y: ($event.pointerPosition.y - rect_palette.y + parent.scrollTop) * zoom_factor,
    }



    if (this.offset == null) {

      this.offset = {
        x: scaled_pointer.x - op_topleft_inscale.x,
        y: scaled_pointer.y - op_topleft_inscale.y
      }
      //console.log("LEFT WITH SCALE VS, LEFT POINTER ", op_topleft_inscale, scaled_pointer, this.offset);

    }


    this.topleft = {
      x: scaled_pointer.x - this.offset.x,
      y: scaled_pointer.y - this.offset.y

    }
    note_container.style.transform = 'none'; //negate angulars default positioning mechanism
    note_container.style.top = this.topleft.y + "px";
    note_container.style.left = this.topleft.x + "px";

  }

  dragEnd($event: CdkDragEnd) {

    //CATCH THE CASE WHERE THIS IS DROPPED OUTSIDE OF SELECTABLE AREA

    let op_container = document.getElementById('note-' + this.id);


    this.topleft = {
      x: (op_container.offsetLeft < 0) ? 0 : this.topleft.x,
      y: (op_container.offsetTop < 0) ? 0 : this.topleft.y,

    }

    op_container.style.transform = 'none'; //negate angulars default positioning mechanism
    op_container.style.top = this.topleft.y + "px";
    op_container.style.left = this.topleft.x + "px";




    this.notes.setPosition(this.id, this.topleft);
    this.onNoteMoved.emit();

    const change: NoteStateMove = {
      originator: 'NOTE',
      type: 'MOVE',
      id: this.id,
      before: this.starting_topleft,
      after: this.topleft
    }

    this.ss.addStateChange(change);
  }


  /**
   * Called when main palette is rescaled and triggers call to rescale this element, and update its position 
   * so it remains at the same coords. 
   * @param scale - the zoom scale of the iterface (e.g. the number of pixels to render each cell)
   */
  rescale() {

  }

  save() {
    if (this.noteForm.valid && this.note) {
      // Update the note object with form values

      const change: NoteValueChange = {
        originator: 'NOTE',
        type: 'UPDATED',
        id: this.id,
        before: this.before,
        after: this.note
      }

      this.ss.addStateChange(change);

      // this.before = this.notes.copyNote(this.note);
      this.saveNoteText.emit();
    }

    this.noteForm.markAsPristine();
  }


  //called from an undo event
  updateValues(note: Note) {
    this.note.title = note.title;
    this.note.text = note.text;
    this.note.imageurl = note.imageurl;

    this.noteForm.patchValue({
      title: note.title,
      text: note.text,
      imageurl: note.imageurl
    }, { emitEvent: false });
  }


  /**
   * draw onto the supplied canvas, to be used when printing
   * @returns 
   */
  drawForPrint(canvas, cx, scale: number) {

    // if(canvas === undefined) return;



    // //draw the supplemental info like size
    // cx.fillStyle = "#666666";
    // cx.font = scale*2+"px Verdana";
    // cx.fillText(this.note.text,this.bounds.topleft.x, this.bounds.topleft.y+this.bounds.height + 20 );

  }

  disableDrag() {
    this.disable_drag = true;
  }

  enableDrag() {
    this.disable_drag = false;
  }

  uploadImage() {
    this.show_url = true;

  }

  enterUrl() {
    this.show_url = false;
    this.save();
  }

  colorChange(event: any) {
    this.notes.setColor(this.id, event);
  }

  hidePreview(e) { console.log(e.getContent()); }


}
